<script setup lang="ts">
import merge from 'lodash/merge';

interface FormTrueFalse
{
    content: {
        value: {
            description: string;
            questions: Question[];
        };
    };
}

interface Question
{
    id: number;
    text: string;
    isTrue: boolean;
}

interface Props
{
    modelValue: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    'update:modelValue': [value: any];
}>();

const defaults: any = {
    description: '',
    questions: []
};

const form = computed((): FormType<FormTrueFalse> =>
{
    return props.modelValue;
});

onBeforeMount(() =>
{
    form.value.content.value = merge(defaults, form.value.content.value);
    form.value.file = null;

    if (!form.value.content.value.questions.length)
    {
        form.value.content.value.questions = [{
            id: 1,
            text: '',
            isTrue: true
        }];
    }
});

function addQuestion(): void
{
    const ids = form.value.content.value.questions.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.questions.length > 0 ? Math.max(...ids) : 1;

    form.value.content.value.questions.push({
        id: maxId + 1,
        text: '',
        isTrue: false
    });
}

function removeQuestion(index: number): void
{
    const items = form.value.content.value.questions.slice();

    items.splice(index, 1);
    form.value.content.value.questions = items;
}

function isTrueChanged(id: number): void
{
    const clickedQuestionValue = form.value.content.value.questions.find((x: { id: number }) => x.id == id).isTrue;

    form.value.content.value.questions.find((x: { id: number }) => x.id == id).isTrue = !clickedQuestionValue;
}

function questionUp(index: number): void
{
    if (index > 0)
    {
        const questions = form.value.content.value.questions.splice(index, 1);

        form.value.content.value.questions.splice(index - 1, 0, questions[0]);

        form.value.$errors.clear();
    }
}

function questionDown(index: number): void
{
    if (index < form.value.content.value.questions.length - 1)
    {
        const questions = form.value.content.value.questions.splice(index, 1);

        form.value.content.value.questions.splice(index + 1, 0, questions[0]);

        form.value.$errors.clear();
    }
}

watch(() => form.value.content.value, (value: any) =>
{
    triggetModel();
}, { deep: true });

function triggetModel(): any
{
    form.value.$errors.clear('content.value.description');

    emit('update:modelValue', form.value);
}
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('content.value.description')" :state="form.$errors.state('content.value.description')" required>
        <RichTextEditor
            :id="seconds('description-' + form.id)"
            v-model="form.content.value.description"
            name="description"
            shortcode
            timeline
        />
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Stwierdzenia]]]')" :invalid-feedback="form.$errors.first('content.questions')" :state="form.$errors.state('content.questions')">
        <div v-for="(question, i) in form.content.value.questions" :key="question.id" class="mb-3 g-0 row">
            <div class="col-md-11 col-sm-10">
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text bg-primary border-primary text-white">{{ i + 1 }}</span>
                    </div>
                    <IdeoFormTextarea v-model="question.text" :name="`content.value.questions[${i}]`" :rows="2" :placeholder="$t('[[[Pytanie]]]')" :data-cy="`question-${i}`" />
                    <div class="input-group-append">
                        <ActionButton :variant="question.isTrue ? 'success' : 'secondary'" icon="far fa-check" :title="$t('[[[Poprawna odpowiedź]]]')" data-cy="isTrue" @click="isTrueChanged(question.id)" />
                        <ActionButton
                            variant="danger"
                            icon="far fa-trash"
                            :title="$t('[[[Usuń pytanie]]]')"
                            :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                            :disabled="form.content.value.questions.length < 2"
                            @click="removeQuestion(i)"
                        />
                    </div>
                </div>
                <small v-if="!form.$errors.state(`content.value.questions[${i}]`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}]`) }}</small>
            </div>
            <div class="col-md-1 col-sm-2 d-flex align-items-baseline">
                <ActionButton
                    variant="secondary"
                    icon="far fa-arrow-up"
                    class="ms-auto"
                    :title="$t('[[[Wyżej]]]') "
                    :disabled="i == 0"
                    @click="questionUp(i)"
                />
                <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Niżej]]]')" :disabled="i == form.content.value.questions.length - 1" @click="questionDown(i)" />
            </div>
        </div>
        <div>
            <ActionButton
                variant="outline-dark"
                class="w-100"
                icon="far fa-plus"
                :text="$t('[[[Dodaj stwierdzenie]]]')"
                data-cy="addQuestion"
                @click="addQuestion()"
            />
        </div>
    </IdeoFormGroup>
</template>
